import { doDefaultFetch } from '@nhl/fetch-helpers';
import { constants } from '@nhl/formatter-utilities';

/*
@return json example:
{
	id: 2015010001,
	visitingTeam: {
		id: 15
	},
	homeTeam: {
		id: 12
	},
	gameDate: "2015-09-20"
}
 */

const fetchCalendarData = args => {
    const includeJson = [
        'id',
        'gameDate',
        'season.id',
        'state.id',
        'gameScheduleState.id',
        {
            visitingTeam: [
                'id',
                'fullName',
                'placeName',
                'commonName',
                'triCode',
                {
                    division: [
                        'id',
                        'name'
                    ],
                },
                {
                    conference: [
                        'id',
                        'name'
                    ],
                },
            ]
        },
        {
            homeTeam: [
                'id',
                'fullName',
                'placeName',
                'commonName',
                'triCode',
                {
                    division: [
                        'id',
                        'name'
                    ],
                },
                {
                    conference: [
                        'id',
                        'name',
                    ],
                },
            ],
        },
    ];

    return doDefaultFetch(
        '/site/api/game' +
        `?cayenneExp=gameDate>="${args.startDate}" and gameDate<"${args.endDate}"` +
        `&include=${JSON.stringify(includeJson)}`,
        args
    );
};

/*

Model should match gameList/propTypes
@return json example:
{
	id: 2016020559,
	arenaName:  "BMO Field",
	easternStartTime:   "15:30",
	gameDate:   "2017-01-01",
	period: 4,
	homeScore: 5,
	homeTeam: {
		abbreviation:   "TOR"
		commonName:     "Maple Leafs"
		placeName:      "Toronto"
	},
	hfdEvents: [
		{
			period: 1,
			player1Id: 8468083,
			player1TeamId: 17,
			timeInPeriod: "00:00",
			typeCode: 502,
		}
	],
	rtSummary: {
		awaySog: 55,
		homeSog: 25
	},
	state: {
		id: 7
	},
	timeRemaining:  "FINAL",
	venue:  "ANAHEIM",
	visitingScore: 3,
	visitingTeam: {
		abbreviation:   "PHI",
		commonName:     "Flyers",
		placeName:      "Philadelphia"
	}
}
*/

const fetchGameData = args => {
    return doDefaultFetch(
        `/site/api/game/withDefaultData` +
        `?cayenneExp=gameDate>="${args.startDate}" and gameDate<"${args.endDate}" and gameType in (${constants.REGULAR_SEASON}, ${constants.PRE_SEASON}, ${constants.PLAYOFFS}, ${constants.ALL_STAR}, ${constants.WOMENS_ALL_STAR}, ${constants.FOUR_NATIONS_ROUND_ROBIN}, ${constants.FOUR_NATIONS_PLAYOFFS})` +
        `&include=id` +
        `&include=visitingTeam.stats.id` +
        `&include=visitingTeam.stats.gameType` +
        `&include={"path":"visitingTeam.stats", "cayenneExp":"season=${args.season}"}` +
        `&include=homeTeam.stats.id` +
        `&include=homeTeam.stats.gameType` +
        `&include={"path":"homeTeam.stats", "cayenneExp":"season=${args.season}"}` +
        `&include=playoffTeamGames` +
        `&include=playoffTeamGames.team.triCode` +
        `&include=playoffTeamGames.opponentTeam.triCode` +
        `&include=playoffTeamGames.seriesWinningTeam.triCode` +
        `&include=specialEventSeason.sponsorPrefix` +
        `&include=specialEventSeason.specialEvent.name`,
        args
    );
};

const fetchXMostRecentGames = args => {
    return doDefaultFetch(
        `/site/api/game/withDefaultData` +
        `?include=id` +
        `&sort=gameDate` +
        `&dir=DESC` +
        `&limit=${args.gamesToFetch}`,
        args
    );
};

const fetchXNextGamesByTeamId = args => {
    return doDefaultFetch(
        `/site/api/game/withDefaultData` +
        `?include=id` +
        `&cayenneExp=gameDate>="${args.minimumGameDate}" and (visitingTeam = ${args.teamId} or homeTeam = ${args.teamId})` +
        `&sort=gameDate` +
        `&dir=ASC` +
        `&limit=${args.gamesToFetch}`,
        args
    );
};

const fetchGameNotesForTeam = args => {
    const firstSeasonWithNotes = 20092010;

    return doDefaultFetch(
        `/site/api/game` +
        `?include=id` +
        `&include=notes` +
        `&include=gameDate` +
        `&include=homeTeam.id` +
        `&include=homeTeam.fullName` +
        `&include=visitingTeam.id` +
        `&include=visitingTeam.fullName` +
        `&cayenneExp=season >= ${firstSeasonWithNotes} and (homeTeam = ${args.teamId} or visitingTeam = ${args.teamId})` +
        `&sort=gameDate` +
        `&dir=DESC`,
        args
    );
};

const fetchGameDataById = args => {
    // doing some trickery to get season and gameType for the team stats:
    // Start of
    // season id   Game type   Game number
    //  |           |           |
    // ----         --         ----
    // |  |         ||         |  |
    // 2012         02         0001
    // So we substring the first four characters of the game id to get part 1 of the season id.
    // Then we add one to the that number to get part 2 of the season id.
    // Then we concatenate the two numbers to get the season id. For the above game that would be 20122013.
    //
    // Then we do a substring on the game type string (which also truncates leading 0s) to get the game type.
    // Then we can use these peices of data in our query.
    const stringGameId = args.gameId + '';
    const seasonStart = parseFloat(stringGameId.substring(0, 4));
    const seasonEnd = seasonStart + 1;
    const season = seasonStart + '' + seasonEnd;
    const gameType = parseFloat(stringGameId.substring(4, 6));

    return doDefaultFetch(
        `/site/api/game/boxscore?cayenneExp=id=${args.gameId}` +
        `&include=id` +
        `&include=visitingTeam.stats.id` +
        `&include=visitingTeam.stats.gameType` +
        `&include={"path":"visitingTeam.stats", "cayenneExp":"season=${season} and gameType=${gameType}"}` +
        `&include=homeTeam.stats.id` +
        `&include=homeTeam.stats.gameType` +
        `&include={"path":"homeTeam.stats", "cayenneExp":"season=${season} and gameType=${gameType}"}` +
        // hfdEvents
        `&include=hfdEvents` +
        // hfdRosterSpots
        `&include=hfdRosterSpots` +
        // hfdStars
        `&include=hfdStars.player` +
        `&include=hfdStars.player.position.positionCode` +
        `&include=hfdStars.team.id` +
        `&include=hfdStars.team.triCode` +
        `&include=hfdStars.team.logos` +
        `&include=hfdStars.team.league.abbreviation` +
        // hfdGoalieGameStats
        `&include=hfdGoalieGameStats.player` +
        `&include=hfdGoalieGameStats.player.position.positionCode` +
        `&include=hfdGoalieGameStats` +
        // hfdSkaterGameStats
        `&include=hfdSkaterGameStats.player` +
        `&include=hfdSkaterGameStats.player.position.positionCode` +
        `&include=hfdSkaterGameStats` +
        // hfdTeamGameStats
        `&include=hfdTeamGameStats` +
        `&include=hfdTeamGameStats.team.id` +
        `&include=hfdTeamGameStats.team.triCode` +
        `&include=hfdTeamGameStats.team.logos` +
        // hfdTeamPeriodStats
        `&include=hfdTeamPeriodStats` +
        `&include=hfdTeamPeriodStats.team.id` +
        // hfdPlayerTotalTimeOnceIce
        `&include=hfdPlayerTotalTimeOnIce` +
        `&include=hfdPlayerTotalTimeOnIce.player` +
        `&include=hfdPlayerTotalTimeOnIce.player.position.positionCode` +
        // playoffTeamGame
        `&include=playoffTeamGames` +
        `&include=playoffTeamGames.team.triCode` +
        `&include=playoffTeamGames.opponentTeam.triCode` +
        `&include=playoffTeamGames.seriesWinningTeam.triCode` +
        `&include=specialEventSeason.sponsorPrefix` +
        `&include=specialEventSeason.specialEvent.name`,
        args
    );
};

const fetchPastGameDataById = args => {
    const stringGameId = args.gameId + '';
    const seasonStart = parseFloat(stringGameId.substring(0, 4));
    const seasonEnd = seasonStart + 1;
    const season = seasonStart + '' + seasonEnd;
    const gameType = parseFloat(stringGameId.substring(4, 6));

    return doDefaultFetch(
        `/site/api/game/boxscore?cayenneExp=id=${args.gameId}`+
        `&include=id` +
        `&include=visitingTeam.stats.id` +
        `&include={"path":"visitingTeam.stats", "cayenneExp":"season=${season} and gameType=${gameType}"}` +
        `&include=homeTeam.stats.id` +
        `&include={"path":"homeTeam.stats", "cayenneExp":"season=${season} and gameType=${gameType}"}` +
        // stars
        `&include=firstStar` +
        `&include=firstStar.position.positionCode` +
        `&include=secondStar` +
        `&include=secondStar.position.positionCode` +
        `&include=thirdStar` +
        `&include=thirdStar.position.positionCode` +
        // goals
        `&include=goals` +
        // shootout
        `&include=shootoutDetails` +
        // overtime
        `&include=overtime` +
        // goalieByGameStats
        `&include=goalieByGameStats` +
        `&include=goalieByGameStats.player` +
        `&include=goalieByGameStats.player.position.positionCode` +
        `&include=goalieByGameStats.team.id` +
        `&include=goalieByGameStats.team.triCode` +
        `&include=goalieByGameStats.team.logos` +
        // playerByGameStats
        `&include=playerByGameStats` +
        `&include=playerByGameStats.player` +
        `&include=playerByGameStats.player.position.positionCode` +
        `&include=playerByGameStats.team.id` +
        `&include=playerByGameStats.team.triCode` +
        `&include=playerByGameStats.team.logos` +
        // teamPeriodStats
        `&include=teamPeriodStats` +
        `&include=teamPeriodStats.team.id` +
        // playoffTeamGame
        `&include=playoffTeamGames` +
        `&include=playoffTeamGames.team.triCode` +
        `&include=playoffTeamGames.opponentTeam.triCode` +
        `&include=playoffTeamGames.seriesWinningTeam.triCode` +
        `&include=specialEventSeason.sponsorPrefix` +
        `&include=specialEventSeason.specialEvent.name`,
        args
    );
};

const fetchGameStateDataById = (gameId) => {
    const includes = ['id', 'state.id'];
    return doDefaultFetch(
        `/site/api/game?cayenneExp=id=${gameId}`+
        `&include=${JSON.stringify(includes)}`,
    );
};

const fetchServerTime = args => {
    return doDefaultFetch('/site/api/serverTime', args);
};

/*
 @return
 A gameByDate object with games by date range.
 ie.
 {
 "2016-06-05": [ { game },  { game } ],
 "2016-06-06": [ ... ]
 }
 */
export const generateGameListByDate = (games, selectedTeams, selectedFilters, skipFiltering) => {
    if(games.length > 0 && selectedTeams.length > 0){
        const gamesByDate = {};

        const gamesByDateTally = (gamesByDate, game) => {
            if (!gamesByDate[game.gameDate]) {
                gamesByDate[game.gameDate] = [];
            }
            gamesByDate[game.gameDate].push(game);
        };

        games.forEach((game) => {
            if(selectedTeams.length === 0 || skipFiltering){
                gamesByDateTally(gamesByDate, game);
            } else {
                const teamInArray = selectedTeams.filter(team => {
                    if(selectedFilters.includes('away') && selectedFilters.includes('home')){
                        return team.id === game.visitingTeam.id || team.id === game.homeTeam.id;
                    } else if(selectedFilters.includes('away')) {
                        return team.id === game.visitingTeam.id;
                    } else if(selectedFilters.includes('home')) {
                        return team.id === game.homeTeam.id;
                    } else {
                        return team.id !== game.visitingTeam.id || team.id !== game.homeTeam.id;
                    }
                });

                if(teamInArray.length > 0) {
                    gamesByDateTally(gamesByDate, game);
                }
            }
        });

        return gamesByDate;
    } else {
        return {};
    }
};

/*
 @return
 A dictionary of games by date
 ie.
 numberOfGamesByDate = {
 '2015-09-03': 6, ...
 };
 */
export const getNumberOfEventsByDate = (games, selectedTeams, selectedFilters, skipFiltering) => {
    if (games.length > 0 && selectedTeams.length > 0 && selectedFilters.length > 0) {
        const gameTally = (gamesList, game) => {
            if (!gamesList[game.gameDate]) {
                gamesList[game.gameDate] = 1;
            } else {
                gamesList[game.gameDate] = gamesList[game.gameDate] + 1;
            }
        };

        return games.reduce((gamesList, game) => {
            if(selectedTeams.length === 0 || skipFiltering){
                gameTally(gamesList, game);

            } else {
                const teamInArray = selectedTeams.filter(team => {
                    let toReturn = false;

                    if (selectedFilters.includes('home')) {
                        toReturn = toReturn || team.id === game.homeTeam.id;
                    }

                    if (selectedFilters.includes('away')) {
                        toReturn = toReturn || team.id === game.visitingTeam.id;
                    }

                    return toReturn;
                });

                if(teamInArray.length > 0) {
                    gameTally(gamesList, game);
                }
            }
            return gamesList;
        }, {});
    } else {
        return {};
    }
};

export default {
    fetchCalendarData,
    fetchGameData,
    fetchGameDataById,
    fetchXMostRecentGames,
    fetchXNextGamesByTeamId,
    fetchGameNotesForTeam,
    fetchPastGameDataById,
    fetchGameStateDataById,
    fetchServerTime
};
